import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rp } from "@/lib/payroll";
import { TID } from "@/constants/testIds";
import { useT } from "@/i18n";
import NumField from "@/components/NumField";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useIsMobile } from "@/lib/useIsMobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FIELD =
  "focus-ring h-11 w-full rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3.5 text-[15px] placeholder:text-[#B0B0B8]";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { employee_id: "", tipe: "masuk", tanggal: today(), nominal: 0, keterangan: "" };

const fmtDate = (s) => {
  if (!s) return "\u2014";
  const [y, m, d] = String(s).slice(0, 10).split("-");
  if (!y || !m) return String(s);
  return `${(d || "01").padStart(2, "0")}/${m.padStart(2, "0")}/${y.slice(-2)}`;
};

export default function TitipBalance() {
  const { t } = useT();
  const [accounts, setAccounts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [detailFor, setDetailFor] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [delEntry, setDelEntry] = useState(null);
  const { readOnly } = useAuth();
  const isMobile = useIsMobile();

  const loadAccounts = async () => {
    try {
      const { data } = await api.get("/titip");
      setAccounts(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, []);

  const openDetail = async (eid) => {
    setDetailFor(eid);
    setLedger(null);
    try {
      const { data } = await api.get(`/titip/${eid}`);
      setLedger(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const refreshDetail = async () => {
    if (detailFor) {
      const { data } = await api.get(`/titip/${detailFor}`);
      setLedger(data);
    }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.employee_id) return toast.error(t("sav.selectEmpFirst"));
    if (!(Number(form.nominal) > 0)) return toast.error(t("sav.amountGt0"));
    setBusy(true);
    try {
      await api.post("/titip", {
        employee_id: form.employee_id,
        tipe: form.tipe,
        tanggal: form.tanggal || today(),
        nominal: Number(form.nominal) || 0,
        keterangan: form.keterangan || "",
      });
      setAddOpen(false);
      setForm(EMPTY);
      await loadAccounts();
      await refreshDetail();
      toast.success(t("sav.saved"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async () => {
    if (!delEntry) return;
    try {
      await api.delete(`/titip/entry/${delEntry.id}`);
      await loadAccounts();
      await refreshDetail();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDelEntry(null);
    }
  };

  const totalTitip = useMemo(
    () => accounts.reduce((a, x) => a + x.balance, 0),
    [accounts]
  );

  return (
    <div data-testid={TID.titip.root} className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[13.5px] font-medium text-[#8E8E93]">
            {t("sav.subtitle")}
          </p>
          <h1 className="text-[26px] font-semibold tracking-tight sm:text-[30px]">{t("sav.title")}</h1>
        </div>
        {!readOnly && (
        <button
          data-testid={TID.titip.add}
          onClick={() => {
            setForm(EMPTY);
            setAddOpen(true);
          }}
          className="flex h-10 items-center gap-1.5 rounded-full bg-[#0071E3] px-4 text-[14px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98]"
        >
          <Plus className="h-4 w-4" /> {t("sav.entry")}
        </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-24 text-[14px] text-[#8E8E93]">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="card-soft grid place-items-center gap-2 px-6 py-20 text-center">
          <Wallet className="h-7 w-7 text-[#B0B0B8]" />
          <p className="text-[15px] font-medium">{t("sav.noneTitle")}</p>
          <p className="max-w-xs text-[13.5px] text-[#8E8E93]">
            {t("sav.noneDesc")}
          </p>
        </div>
      ) : (
        <>
          <div className="card-soft flex items-center justify-between px-5 py-4">
            <span className="label">{t("sav.totalHeld")}</span>
            <span className="num text-[20px] font-semibold tracking-tight">Rp {rp(totalTitip)}</span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {accounts.map((a) => (
              <button
                key={a.employee_id}
                data-testid={TID.titip.card(a.employee_id)}
                onClick={() => openDetail(a.employee_id)}
                className="card-soft group px-5 py-4 text-left transition-all hover:shadow-md active:scale-[0.99]"
              >
                <p className="truncate text-[15px] font-semibold tracking-tight">{a.nama}</p>
                <p className="label mt-3">{t("sav.balance")}</p>
                <p className="num text-[24px] font-semibold tracking-tight text-[#0071E3]">
                  Rp {rp(a.balance)}
                </p>
                <div className="mt-2 flex gap-4 text-[12px] text-[#8E8E93]">
                  <span className="num">{t("sav.in")} Rp {rp(a.masuk)}</span>
                  <span className="num">{t("sav.out")} Rp {rp(a.keluar)}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Add manual entry */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent
          data-testid={TID.titip.dialog}
          className="gap-0 rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[440px]"
        >
          <DialogHeader className="text-left">
            <DialogTitle className="text-[19px] font-semibold tracking-tight">
              {t("sav.entryTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-5 grid gap-4">
            <div className="space-y-1.5">
              <label className="label block">{t("sav.employee")}</label>
              <Select value={form.employee_id} onValueChange={set("employee_id")}>
                <SelectTrigger
                  data-testid={TID.titip.emp}
                  className="h-11 rounded-xl border-[#E1E1E6] bg-[#FAFAFA] text-[15px]"
                >
                  <SelectValue placeholder={t("sav.selectEmp")} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] rounded-xl">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.nama}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="label block">{t("sav.type")}</label>
                <Select value={form.tipe} onValueChange={set("tipe")}>
                  <SelectTrigger
                    data-testid={TID.titip.tipe}
                    className="h-11 rounded-xl border-[#E1E1E6] bg-[#FAFAFA] text-[15px]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="masuk">{t("sav.inPlus")}</SelectItem>
                    <SelectItem value="keluar">{t("sav.outMinus")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="label block">{t("sav.date")}</label>
                <input
                  data-testid={TID.titip.tanggal}
                  type="date"
                  value={form.tanggal}
                  onChange={(e) => set("tanggal")(e.target.value)}
                  className={FIELD}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("sav.amount")}</label>
              <NumField
                testid={TID.titip.nominal}
                value={form.nominal}
                onChange={set("nominal")}
                align="text-left"
                className="h-11 w-full text-[15px]"
              />
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("sav.noteOpt")}</label>
              <input
                data-testid={TID.titip.keterangan}
                value={form.keterangan}
                onChange={(e) => set("keterangan")(e.target.value)}
                placeholder={t("sav.notePh")}
                className={FIELD}
              />
            </div>
          </div>
          <button
            data-testid={TID.titip.save}
            onClick={submit}
            disabled={busy}
            className="mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#0071E3] text-[15px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.99] disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("sav.save")}
          </button>
        </DialogContent>
      </Dialog>

      {/* Ledger detail */}
      <Dialog open={!!detailFor} onOpenChange={(v) => !v && setDetailFor(null)}>
        <DialogContent
          data-testid={TID.titip.detailDialog}
          className="max-h-[88vh] gap-0 overflow-y-auto rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[620px]"
        >
          <DialogHeader className="text-left">
            <DialogTitle className="text-[19px] font-semibold tracking-tight">
              {ledger ? ledger.nama : t("sav.historyTitle")}
            </DialogTitle>
          </DialogHeader>
          {!ledger ? (
            <div className="flex items-center gap-2 py-16 text-[14px] text-[#8E8E93]">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : (
            <>
              <div className="mt-4 flex items-center justify-between rounded-xl bg-[#EEF3FA] px-4 py-3">
                <span className="label">{t("sav.currentBal")}</span>
                <span className="num text-[20px] font-semibold tracking-tight text-[#0071E3]">
                  Rp {rp(ledger.balance)}
                </span>
              </div>
              {isMobile ? (
                <div className="mt-4 space-y-2">
                  {ledger.entries.map((l, idx) => (
                    <div
                      key={l.id || `auto-${idx}`}
                      className="rounded-xl border border-black/[0.06] px-3.5 py-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="num text-[12px] text-[#8E8E93]">{fmtDate(l.tanggal)}</span>
                        <span
                          className={`num text-[14px] font-semibold ${
                            l.tipe === "masuk" ? "text-[#0071E3]" : "text-[#C8102E]"
                          }`}
                        >
                          {l.tipe === "masuk" ? "+" : "-"}
                          {rp(l.nominal)}
                        </span>
                      </div>
                      <div className="mt-1 flex items-end justify-between gap-2">
                        <span className="text-[13px]">
                          {l.keterangan || (l.tipe === "masuk" ? t("sav.inLabel") : t("sav.outLabel"))}
                          {l.sumber === "periode" && (
                            <span className="ml-2 rounded-full bg-[#F2F2F7] px-2 py-0.5 text-[11px] text-[#8E8E93]">
                              {t("sav.auto")}
                            </span>
                          )}
                        </span>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="num text-[12px] text-[#8E8E93]">
                            {t("sav.balanceWord")} {rp(l.saldo)}
                          </span>
                          {l.sumber === "manual" && !readOnly && (
                            <button
                              data-testid={TID.titip.entryDel(l.id)}
                              onClick={() => setDelEntry(l)}
                              className="grid h-7 w-7 place-items-center rounded-full text-[#B0B0B8] transition-colors hover:bg-[#FDF0F2] hover:text-[#C8102E]"
                              aria-label="Delete entry"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {ledger.entries.length === 0 && (
                    <p className="px-3 py-12 text-center text-[14px] text-[#8E8E93]">
                      {t("sav.noTx")}
                    </p>
                  )}
                </div>
              ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[520px] border-collapse">
                  <thead>
                    <tr className="border-b border-black/[0.06]">
                      {[t("sav.colDate"), t("sav.colNote"), t("sav.colAmount"), t("sav.colBalance"), ""].map((h, i) => (
                        <th
                          key={h + i}
                          className={`label whitespace-nowrap px-3 py-2.5 ${
                            i >= 2 && i <= 3 ? "text-right" : "text-left"
                          }`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.entries.map((l, idx) => (
                      <tr key={l.id || `auto-${idx}`} className="border-b border-black/[0.04] last:border-0">
                        <td className="num whitespace-nowrap px-3 py-2.5 text-[13px] text-[#6E6E73]">
                          {fmtDate(l.tanggal)}
                        </td>
                        <td className="px-3 py-2.5 text-[13.5px]">
                          {l.keterangan || (l.tipe === "masuk" ? t("sav.inLabel") : t("sav.outLabel"))}
                          {l.sumber === "periode" && (
                            <span className="ml-2 rounded-full bg-[#F2F2F7] px-2 py-0.5 text-[11px] text-[#8E8E93]">
                              {t("sav.auto")}
                            </span>
                          )}
                        </td>
                        <td
                          className={`num px-3 py-2.5 text-right text-[13.5px] font-medium ${
                            l.tipe === "masuk" ? "text-[#0071E3]" : "text-[#C8102E]"
                          }`}
                        >
                          {l.tipe === "masuk" ? "+" : "-"}
                          {rp(l.nominal)}
                        </td>
                        <td className="num px-3 py-2.5 text-right text-[13.5px]">{rp(l.saldo)}</td>
                        <td className="px-2 py-2.5 text-right">
                          {l.sumber === "manual" && !readOnly && (
                            <button
                              data-testid={TID.titip.entryDel(l.id)}
                              onClick={() => setDelEntry(l)}
                              className="grid h-7 w-7 place-items-center rounded-full text-[#B0B0B8] transition-colors hover:bg-[#FDF0F2] hover:text-[#C8102E]"
                              aria-label="Delete entry"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {ledger.entries.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-12 text-center text-[14px] text-[#8E8E93]">
                          {t("sav.noTx")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delEntry}
        onOpenChange={(v) => !v && setDelEntry(null)}
        title={t("sav.delTitle")}
        description={
          delEntry
            ? (delEntry.tipe === "masuk"
                ? t("sav.delDescIn", { amt: rp(delEntry.nominal) })
                : t("sav.delDescOut", { amt: rp(delEntry.nominal) }))
            : ""
        }
        onConfirm={removeEntry}
        testid={TID.titip.delDialog}
        confirmTestid={TID.titip.delConfirm}
      />
    </div>
  );
}
